// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useSparseFulfillmentList } from '@/fulfillment/hooks/use-sparse-fulfillment-list';
import { SparseFulfillment } from '@/sparse/api/types';
import { Autocomplete, Box, Chip, Stack, TextField } from '@mui/material';
import { ForwardedRef, forwardRef, useEffect, useMemo } from 'react';

interface Props {
  /**
   * The name attribute for the form input element.
   * Used for form integration and accessibility.
   */
  name?: string;

  /**
   * The label text displayed above the input element.
   * Should clearly indicate the purpose of the search field.
   */
  label?: string;

  /**
   * If true, displays the component in an error state with red highlighting.
   * Used for form validation feedback.
   */
  error?: boolean;

  /**
   * Text displayed below the input element.
   * Used for additional context or error messages.
   */
  helperText?: string;

  /**
   * The currently selected fulfillment order in the search field.
   * Null indicates no selection.
   */
  value: SparseFulfillment | null;

  /**
   * If true, filters out third-party fulfilled orders from the results.
   * Defaults to false.
   */
  ignore3pl?: boolean;

  /**
   * If true, the input field is disabled and cannot be interacted with.
   */
  disabled?: boolean;

  /**
   * Callback fired when a fulfillment order is selected or deselected.
   * Receives the selected fulfillment or null if selection is cleared.
   */
  onChange: (value: SparseFulfillment | null) => void;
}

/**
 * A component that renders a searchable fulfillment order selector with autocomplete.
 *
 * Features:
 * - Real-time search with fuzzy matching
 * - Automatic fetching of draft/partially filled fulfillment orders
 * - Optional filtering of 3PL fulfillments
 * - Form integration with error states
 * - Accessibility support via ARIA attributes
 *
 * Search behavior:
 * - Shows fulfillment orders that can still take a shipment
 * - Searches by FO number
 * - Can optionally filter out third-party fulfillments
 *
 * @param props - Component properties
 * @param ref - Forwarded ref to the input element
 */
function FulfillmentSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { fulfillments, isLoading } = useSparseFulfillmentList({
    statuses: [
      'DRAFT',
      'AWAITING_3PL',
      'OPEN_PARTIAL',
      'OPEN_FULL',
      'OPEN_OVERAGE',
      'SHIPPED_PARTIAL',
      'SHIPPED_FULL',
      'SHIPPED_OVERAGE',
      'DELIVERED_PARTIAL',
    ],
    ignore3pl: props.ignore3pl ?? false,
  });

  // Extract props for useEffect dependency tracking
  const { value, onChange } = props;

  // A closed order takes no further shipments. The order this shipment already
  // names stays listed so the field can still show it.
  const selectableFulfillments = useMemo(
    () =>
      fulfillments.filter((fo) => fo.closedAt == null || fo.id === value?.id),
    [fulfillments, value]
  );

  // Auto-resolve stub values to full records after data loads
  // A stub is a value with id/key set but an empty order number (indicating incomplete data)
  useEffect(() => {
    // Skip if no value or value is already complete (has an order number)
    if (!value || value.fulfillmentOrderNumber) {
      return;
    }

    // Value is a stub - find the full record by id or key
    const match = selectableFulfillments.find(
      (fo) => fo.id === value.id || fo.key === value.key
    );

    if (match) {
      onChange(match);
    }
  }, [selectableFulfillments, value, onChange]);

  return (
    <Autocomplete
      ref={ref}
      size="small"
      value={props.value}
      options={selectableFulfillments}
      loading={isLoading}
      loadingText="Loading..."
      disabled={props.disabled}
      getOptionLabel={(option) => option.fulfillmentOrderNumber}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(optionProps, option) => (
        <Box component="li" {...optionProps} key={option.id}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <span>{option.fulfillmentOrderNumber}</span>
            {option.isThirdPartyFulfilled && <Chip label="3PL" size="small" />}
          </Stack>
        </Box>
      )}
      onChange={(_, value) => props.onChange(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          label={props.label}
          error={props.error}
          helperText={isLoading ? 'Loading...' : props.helperText}
        />
      )}
    />
  );
}

/**
 * A controlled component for searching and selecting fulfillment orders.
 * Wraps the FulfillmentSearchRenderer with ref forwarding for form integration.
 * See the renderer documentation for full feature details.
 */
export const FulfillmentSearch = forwardRef(FulfillmentSearchRenderer);
